import type { GetExamsByIdsQuery } from '@exam-room/graphql';
import { omit } from '@exam-room/utils';

import { type ExamEvent } from '../../../providers';

import { type ExamWithEvent, type Exam, type QConfigStructure } from './types';

export function formatTimeToDigit(date: Date) {
  return date.toLocaleTimeString('th-TH', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// TODO: Change status to match the API
export function getStatusLabel(status: 'preparing' | 'waiting' | 'completed') {
  switch (status) {
    case 'preparing':
      return 'instruction.examDetails.status.preparing';
    case 'waiting':
      return 'instruction.examDetails.status.waiting';
    case 'completed':
      return 'instruction.examDetails.status.completed';
  }
}

function mapExamData(exam: GetExamsByIdsQuery['erGetExamsByIds'][number]) {
  return {
    id: exam.id,
    title: exam.title,
    totalQuestions: exam.totalQuestions,
    structures: exam.metadata.qConfigStructureParts
      .slice()
      .sort((a: QConfigStructure, b: QConfigStructure) => a.order - b.order)
      .map((structure: QConfigStructure) => ({
        id: structure.id,
        name: structure.name,
        order: structure.order,
        totalQuestions: structure.totalQuestions,
        passingCriteria: structure.passingCriteria,
      })),
  };
}

export function mapExams(
  examEvents: ExamEvent['metadata']['events'],
  exams: Exam[],
  closestExamOrder: number,
): ExamWithEvent[] {
  return [...examEvents]
    .sort((a, b) => a.order - b.order)
    .map((examEvent) => {
      const exam = exams.find((exam) => exam.id === examEvent.examId);

      if (!exam) {
        throw new Error('Incomplete exam data');
      }

      const examWithEvent = { ...omit(examEvent, ['examId']), ...mapExamData(exam) };

      if (examEvent.order < closestExamOrder) {
        return { ...examWithEvent, status: 'completed' };
      }
      if (examEvent.order === closestExamOrder) {
        return { ...examWithEvent, status: 'preparing' };
      }

      return { ...examWithEvent, status: 'waiting' };
    });
}
